/**
 * Modal for creating or editing a POC assignment rule configuration.
 * Delegates form state to usePocAssignmentForm and renders section sub-components.
 */

import { Modal, TextInput } from "risa-oasis-ui_v2";
import type { PocAssignmentRuleFormData } from "../../../data-model/pocAssignmentRule";
import type { QueryBuilderConfig } from "../../../data-model/queryBuilder";
import { ModalId } from "../../../enums/modalId";
import ActiveToggleSection from "../shared/ActiveToggleSection";
import { usePaRequestsActionFieldOptions } from "../shared/usePaRequestsActionFieldOptions";
import PocAssignmentActionsSection from "./PocAssignmentActionsSection";
import PocAssignmentTriggersSection from "./PocAssignmentTriggersSection";
import { usePocAssignmentForm } from "./usePocAssignmentForm";

interface AddNewPocAssignmentModalProps {
  /** Condition query builder config (includes top_values per field). */
  conditionConfig: QueryBuilderConfig;
  onCancel: () => void;
  onClose: () => void;
  onSave: (data: PocAssignmentRuleFormData) => void | Promise<void>;
  /** When provided, form is pre-filled and modal is in edit mode. */
  initialData?: PocAssignmentRuleFormData;
}

const AddNewPocAssignmentModal = ({
  conditionConfig,
  onCancel,
  onClose,
  onSave,
  initialData,
}: AddNewPocAssignmentModalProps) => {
  const { fieldOptions, valueConditionConfig } =
    usePaRequestsActionFieldOptions();
  const actionValueConfig =
    valueConditionConfig.fields.length > 0
      ? valueConditionConfig
      : conditionConfig;

  const {
    form,
    formKey,
    isSaving,
    isEditMode,
    setForm,
    updateField,
    handleActionFieldChange,
    handleActionTypeChange,
    handleActionSingleValueChange,
    handleActionMultiValueChange,
    handleAddAction,
    handleRemoveAction,
    handleTriggerTypeChange,
    handleTriggerValueChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleClose,
    handleCancel,
    handleSave,
  } = usePocAssignmentForm({ initialData, onSave, onClose, onCancel });

  return (
    <Modal
      dialogId={ModalId.ADD_NEW_POC_ASSIGNMENT_MODAL}
      title={
        isEditMode ? "Edit POC Assignment Rule" : "Add New POC Assignment Rule"
      }
      onCancel={handleCancel}
      onClose={handleClose}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving..." : "Save Configuration"}
      cancelText="Cancel"
      disableSave={isSaving}
    >
      <div
        key={formKey}
        className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1"
      >
        <TextInput
          id="pocAssignmentRuleLabel"
          label="Rule Label"
          placeholder="e.g. G1"
          defaultValue={form.label}
          onChange={(data) => updateField("label", data.value ?? "")}
          onBlur={(data) => updateField("label", data.value ?? "")}
        />
        <TextInput
          id="pocAssignmentRuleSubtext"
          label="Subtext (Optional)"
          placeholder="Short description shown on the rule card"
          defaultValue={form.subtext}
          onChange={(data) => updateField("subtext", data.value ?? "")}
          onBlur={(data) => updateField("subtext", data.value ?? "")}
        />

        <PocAssignmentActionsSection
          conditionConfig={actionValueConfig}
          fieldOptions={fieldOptions}
          actions={form.actions}
          onAddAction={handleAddAction}
          onRemoveAction={handleRemoveAction}
          onTypeChange={handleActionTypeChange}
          onFieldChange={handleActionFieldChange}
          onSingleValueChange={handleActionSingleValueChange}
          onMultiValueChange={handleActionMultiValueChange}
        />

        <PocAssignmentTriggersSection
          triggers={form.triggers}
          onAddTrigger={handleAddTrigger}
          onRemoveTrigger={handleRemoveTrigger}
          onTypeChange={handleTriggerTypeChange}
          onValueChange={handleTriggerValueChange}
        />

        <ActiveToggleSection
          id="pocAssignmentRuleActive"
          checked={form.active}
          onToggle={() =>
            setForm((prev) => ({ ...prev, active: !prev.active }))
          }
        />
      </div>
    </Modal>
  );
};

export default AddNewPocAssignmentModal;
